import { Navigate, Outlet, useRouterState } from '@tanstack/react-router';
import { Menu, PanelLeftOpen } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { PreOnboardingSettingsView } from '@apps/web/components/settings/pre_onboarding_settings_view';
import { SettingsActionBarProvider } from '@apps/web/components/settings/settings_action_bar';
import {
    findSettingsNavItemByPath,
    type SettingsNavItem,
} from '@apps/web/components/settings/settings_nav_items';
import { SettingsLoadingState } from '@apps/web/components/settings/settings_page_shell';
import { SettingsSidebar } from '@apps/web/components/settings/settings_sidebar';
import { useSettingsGates } from '@apps/web/components/settings/use_settings_gates';
import { Button } from '@apps/web/components/ui/button';
import { SidebarProvider, useSidebar } from '@apps/web/components/ui/sidebar';
import {
    isChurnedRestricted,
    isOnboardingRestricted,
} from '@apps/web/lib/customer_lifecycle';
import { EVENTS } from '@apps/web/lib/events';
import {
    useOrganization,
    useUser,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

export function SettingsLayout() {
    const { organizationId, gates, isLoading } = useSettingsGates();
    const posthog = usePostHog();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'settings',
    });
    const { data: user, isLoading: isLoadingUser } = useUser({
        reason: 'settings',
    });
    const pathname = useRouterState({
        select: (state) => state.location.pathname,
    });
    const [actionBarHost, setActionBarHost] = useState<HTMLElement | null>(
        null,
    );
    const currentItem = useMemo(
        () => findSettingsNavItemByPath(pathname),
        [pathname],
    );
    const onboardingRestricted =
        user !== undefined && isOnboardingRestricted(organization, user.admin);
    const churnedRestricted =
        organization !== undefined &&
        isChurnedRestricted(organization, user?.admin);
    const shouldCaptureAnalytics =
        !isLoading &&
        user !== undefined &&
        organizationId !== null &&
        organization !== undefined &&
        !onboardingRestricted;
    const settingsAnalyticsProperties = useMemo(
        () => ({
            organization_id: organizationId,
            settings_pathname: pathname,
            settings_surface: 'redesign',
            settings_tab_id: currentItem?.id ?? null,
            settings_tab_name: currentItem?.label ?? null,
        }),
        [currentItem, organizationId, pathname],
    );

    useEffect(() => {
        if (!shouldCaptureAnalytics) {
            return;
        }
        posthog.capture(EVENTS.SETTINGS_REDESIGN_PAGE_VIEWED, {
            ...settingsAnalyticsProperties,
        });
        const startedAtMs = Date.now();
        return () => {
            posthog.capture(EVENTS.SETTINGS_REDESIGN_PAGE_LEFT, {
                ...settingsAnalyticsProperties,
                duration_ms: Date.now() - startedAtMs,
            });
        };
    }, [posthog, settingsAnalyticsProperties, shouldCaptureAnalytics]);

    const captureTabSelected = useCallback(
        (item: SettingsNavItem) => {
            if (!shouldCaptureAnalytics) {
                return;
            }
            posthog.capture(EVENTS.SETTINGS_REDESIGN_TAB_SELECTED, {
                ...settingsAnalyticsProperties,
                settings_tab_id: item.id,
                settings_tab_name: item.label,
                settings_destination_pathname: item.to,
            });
        },
        [posthog, settingsAnalyticsProperties, shouldCaptureAnalytics],
    );

    const captureSearchPerformed = useCallback(
        (query: string, resultCount: number) => {
            if (!shouldCaptureAnalytics) {
                return;
            }
            posthog.capture(EVENTS.SETTINGS_REDESIGN_SEARCH_PERFORMED, {
                ...settingsAnalyticsProperties,
                search_query: query,
                search_result_count: resultCount,
            });
        },
        [posthog, settingsAnalyticsProperties, shouldCaptureAnalytics],
    );

    // Wait for the profile + user before deciding between the full shell and
    // the constrained pre-onboarding view, so a restricted user never briefly
    // sees the full settings nav. ``isLoading`` also covers the frame where the
    // organization selection has not hydrated yet.
    if (
        isLoading ||
        isLoadingUser ||
        (user === undefined && !churnedRestricted)
    ) {
        return <SettingsLoadingState />;
    }

    if (organizationId !== null && organization === undefined) {
        return <SettingsLoadingState />;
    }

    if (churnedRestricted) {
        return <Navigate to="/home" replace={true} />;
    }

    // Pre-onboarding-restricted orgs (pre-onboarding stage, non-platform-admin)
    // get a team-management-only view, matching the prior settings behavior.
    if (onboardingRestricted) {
        return (
            <PreOnboardingSettingsView
                organizationId={organizationId}
                organizationName={organization?.name}
            />
        );
    }

    return (
        <SettingsActionBarProvider host={actionBarHost}>
            <SidebarProvider className="fixed inset-0 overflow-hidden">
                <SettingsSidebar
                    gates={gates}
                    onSearchPerformed={captureSearchPerformed}
                    onTabSelected={captureTabSelected}
                />
                <SettingsSidebarExpandButton />
                <div className="animate-in fade-in-0 slide-in-from-bottom-1 flex min-h-0 flex-1 flex-col overflow-y-auto duration-150 motion-reduce:animate-none">
                    <SettingsMobileHeader />
                    <Outlet />
                    <div
                        ref={setActionBarHost}
                        className="sticky bottom-0 z-10 flex flex-col pb-[env(safe-area-inset-bottom)] md:pb-0"
                        data-settings-action-bar-host={true}
                    />
                </div>
            </SidebarProvider>
        </SettingsActionBarProvider>
    );
}

function SettingsMobileHeader() {
    const { toggleSidebar } = useSidebar();

    return (
        <header className="border-border bg-background/95 sticky top-0 z-40 flex h-[calc(3.5rem+env(safe-area-inset-top))] items-center gap-3 border-b px-4 pt-[env(safe-area-inset-top)] backdrop-blur md:hidden">
            <Button
                variant="ghost"
                size="icon"
                aria-label="Open settings navigation"
                onClick={toggleSidebar}
                className="-ml-1 h-9 w-9 shrink-0"
            >
                <Menu className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
                <p className="text-foreground truncate text-sm font-semibold">
                    Settings
                </p>
            </div>
        </header>
    );
}

function SettingsSidebarExpandButton() {
    const { isMobile, openMobile, state, toggleSidebar } = useSidebar();
    const shouldShow = !isMobile && !openMobile && state === 'collapsed';

    if (!shouldShow) {
        return null;
    }

    return (
        <Button
            variant="outline"
            size="icon"
            aria-label="Expand sidebar"
            onClick={toggleSidebar}
            className="bg-background fixed top-3 left-3 z-50 h-8 w-8 shadow-sm"
        >
            <PanelLeftOpen className="h-4 w-4" />
        </Button>
    );
}
